import { classNames } from "components/utils";
import React, { useContext, useEffect, useMemo } from "react";
import "./chips.scss";
import { useTheme } from "contexts/ThemeContext";

// Utility functions for color conversion
const hexToRgb = (hex: string) => {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { r, g, b };
};

const rgbToColorMatrix = (r: number, g: number, b: number, opacity = 1) => {
  const normalizedR = r / 255;
  const normalizedG = g / 255;
  const normalizedB = b / 255;
  return `0 0 0 0 ${normalizedR} 0 0 0 0 ${normalizedG} 0 0 0 0 ${normalizedB} 0 0 0 ${opacity} 0`;
};

const hexToGlowMatrix = (hexColor: string, opacity = 1) => {
  const rgb = hexToRgb(hexColor);
  return rgbToColorMatrix(rgb.r, rgb.g, rgb.b, opacity);
};

type ChipProps = {
  selected: boolean;
  onClick: (b: boolean) => void;
  chipName: string;
  manufacturerName: string;
  className?: string;
  style?: React.CSSProperties;
  whiteGlow?: boolean;
  dgx?: boolean;
  withLabel?: string;
  glowColors?: {
    dgx?: string;
    selected?: string;
    deselected?: string;
  };
};

const Chip: React.FC<ChipProps> = ({
  selected,
  onClick,
  chipName,
  manufacturerName,
  className,
  style,
  whiteGlow = false,
  dgx = false,
  withLabel,
  glowColors = {
    dgx: "#E7C86A",
    selected: "#76B900",
    deselected: "#FFFFFF",
  },
}) => {
  const idSuffix = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const themeContext = useTheme();

  glowColors.selected =
    themeContext.theme.highlightColor || glowColors.selected;

  const createGlowFilter = (id: string, hexColor: string, opacity = 1) => (
    <filter
      id={id}
      x="0.116638"
      y="0.116638"
      width="915.017"
      height="915.017"
      filterUnits="userSpaceOnUse"
      colorInterpolationFilters="sRGB"
    >
      <feFlood floodOpacity="0" result="BackgroundImageFix" />
      <feColorMatrix
        in="SourceAlpha"
        type="matrix"
        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
        result="hardAlpha"
      />
      <feOffset />
      <feGaussianBlur stdDeviation="34.1" />
      <feColorMatrix
        type="matrix"
        values={hexToGlowMatrix(hexColor, opacity)}
      />
      <feBlend
        mode="screen"
        in2="BackgroundImageFix"
        result="effect1_dropShadow_0_1"
      />
      <feBlend
        mode="normal"
        in="SourceGraphic"
        in2="effect1_dropShadow_0_1"
        result="shape"
      />
    </filter>
  );

  const DGXGlow = (
    <g filter={`url(#filter0_d_0_1_${idSuffix})`}>
      <rect
        x="74"
        y="74"
        width="767.25"
        height="767.25"
        rx="30"
        fill="url(#paint0_linear_0_1)"
      />
      <rect
        x="71.1583"
        y="71.1583"
        width="772.933"
        height="772.933"
        rx="32.8417"
        stroke="white"
        strokeOpacity="0.2"
        strokeWidth="5.68333"
        strokeLinejoin="round"
        style={{ mixBlendMode: "overlay" }}
      />
    </g>
  );

  const SelectedGlow = (
    <g filter={`url(#filter0_d_0_1_a10on_${idSuffix})`}>
      <rect
        x="74"
        y="74"
        width="767.25"
        height="767.25"
        rx="30"
        fill="url(#paint0_linear_0_1_a10on)"
      />
      <rect
        x="71.1583"
        y="71.1583"
        width="772.933"
        height="772.933"
        rx="32.8417"
        stroke="white"
        strokeOpacity="0.2"
        strokeWidth="5.68333"
        strokeLinejoin="round"
        style={{ mixBlendMode: "overlay" }}
      />
    </g>
  );

  const DeselectedGlow = (
    <g filter={`url(#filter0_d_0_1_a10off_${idSuffix})`}>
      <rect
        x="74"
        y="74"
        width="767.25"
        height="767.25"
        rx="30"
        fill="url(#paint0_linear_0_1_a10off)"
      />
      <rect
        x="71.1583"
        y="71.1583"
        width="772.933"
        height="772.933"
        rx="32.8417"
        stroke="white"
        strokeOpacity="0.2"
        strokeWidth="5.68333"
        strokeLinejoin="round"
        style={{ mixBlendMode: "overlay" }}
      />
    </g>
  );

  const renderChipName = () => {
    const textFill =
      dgx && selected
        ? `url(#gold_gradient_${idSuffix})`
        : `url(#paint1_linear_0_1_a10${selected ? "on" : "off"}_${idSuffix})`;

    if (chipName.length < 5) {
      return (
        <text
          x="50%"
          y="37%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill={textFill}
          fontSize="248px"
          fontWeight="bold"
          className="select-none"
        >
          {chipName}
        </text>
      );
    } else if (chipName.length === 5) {
      return (
        <text
          x="50%"
          y="38%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill={textFill}
          fontSize="200px"
          fontWeight="bold"
          className="select-none"
        >
          {chipName}
        </text>
      );
    } else {
      const words = chipName.split(" ");
      const firstLine = words.slice(0, words.length / 2).join(" ");
      const secondLine = words.slice(words.length / 2).join(" ");

      return (
        <g>
          <text
            x="50%"
            y="32%"
            dominantBaseline="middle"
            textAnchor="middle"
            fill={textFill}
            fontSize="124px"
            fontWeight="bold"
            className="select-none"
          >
            {firstLine}
          </text>
          <text
            x="50%"
            y="42%"
            dominantBaseline="middle"
            textAnchor="middle"
            fill={textFill}
            fontSize="124px"
            fontWeight="bold"
            className="select-none"
          >
            {secondLine}
          </text>
        </g>
      );
    }
  };
  console.log("hello there", style);
  return (
    <div className="flex relative">
      {!!withLabel && (
        <div className="ribbon ribbon-top-right z-50">
          <span>
            <h1 className="font-bold absolute z-50 top-[8px] right-[80px]">
              {withLabel}
            </h1>
          </span>
        </div>
      )}
      <svg
        viewBox="0 0 916 916"
        xmlns="http://www.w3.org/2000/svg"
        className={classNames("cursor-pointer", className ? className : "")}
        onClick={() => onClick(true)}
        style={style}
      >
        <defs>
          {createGlowFilter(
            `filter0_d_0_1_${idSuffix}`,
            glowColors.dgx || "#E7C86A"
          )}
          {createGlowFilter(
            `filter0_d_0_1_a10on_${idSuffix}`,
            glowColors.selected || "#14ACB6"
          )}
          {createGlowFilter(
            `filter0_d_0_1_a10off_${idSuffix}`,
            glowColors.deselected || "#FFFFFF",
            0.4
          )}

          <linearGradient
            id={`gold_gradient_${idSuffix}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="#8C421D" offset="0%" />
            <stop stopColor="#FBE67B" offset="32.53%" />
            <stop stopColor="#FCFBE7" offset="53.55%" />
            <stop stopColor="#F7D14E" offset="76.99%" />
            <stop stopColor="#D4A041" offset="100%" />
          </linearGradient>

          <linearGradient
            id={`paint0_linear_0_1_a10on_${idSuffix}`}
            x1="147.34"
            y1="141.699"
            x2="481.602"
            y2="609.947"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#28272F" />
            <stop offset="1" stopColor="#040404" />
          </linearGradient>

          <linearGradient
            id={`paint1_linear_0_1_a10on_${idSuffix}`}
            x1="207.454"
            y1="307"
            x2="704.983"
            y2="307"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FEFFFF" />
            <stop offset="1" stopColor="#888C93" />
          </linearGradient>

          <linearGradient
            id={`paint2_linear_0_1_a10on_${idSuffix}`}
            x1="129.5"
            y1="653"
            x2="790.5"
            y2="653"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FEFFFF" />
            <stop offset="1" stopColor="#7A7E86" />
          </linearGradient>

          <linearGradient
            id={`paint0_linear_0_1_a10off_${idSuffix}`}
            x1="147.34"
            y1="141.699"
            x2="481.602"
            y2="609.947"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#28272F" />
            <stop offset="1" stopColor="#040404" />
          </linearGradient>

          <linearGradient
            id={`paint1_linear_0_1_a10off_${idSuffix}`}
            x1="523.791"
            y1="306"
            x2="1510.06"
            y2="306"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#28272F" />
            <stop offset="1" stopColor="#040404" />
          </linearGradient>

          <linearGradient
            id={`paint2_linear_0_1_a10off_${idSuffix}`}
            x1="545"
            y1="653"
            x2="1463"
            y2="653"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#28272F" />
            <stop offset="1" stopColor="#040404" />
          </linearGradient>

          <linearGradient
            id={`paint_dgx_text_${idSuffix}`}
            x1="235.933"
            y1="616.588"
            x2="263.74"
            y2="769.167"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#8C421D" />
            <stop offset="0.325272" stopColor="#FBE67B" />
            <stop offset="0.535488" stopColor="#FCFBE7" />
            <stop offset="0.769917" stopColor="#F7D14E" />
            <stop offset="1" stopColor="#D4A041" />
          </linearGradient>
        </defs>

        {dgx && selected
          ? DGXGlow
          : whiteGlow
          ? DeselectedGlow
          : selected
          ? SelectedGlow
          : DeselectedGlow}

        <rect
          x="74"
          y="74"
          width="767.25"
          height="767.25"
          rx="30"
          fill={`url(#paint0_linear_0_1_a10on_${idSuffix})`}
        />
        <rect
          x="71.1583"
          y="71.1583"
          width="772.933"
          height="772.933"
          rx="32.8417"
          stroke="white"
          strokeOpacity="0.2"
          strokeWidth="5.68333"
          strokeLinejoin="round"
          style={{ mixBlendMode: "overlay" }}
        />

        {renderChipName()}

        {manufacturerName.toLowerCase() === "nvidia"
          ? nvidia(selected, idSuffix, dgx)
          : manufacturer(selected, idSuffix, manufacturerName, dgx)}
      </svg>
    </div>
  );
};

export default Chip;

const nvidia = (isOn: boolean, idSuffix: string, isDgx: boolean) => {
  const verticalOffset = isDgx ? -70 : 0; // Adjust this value as needed
  const fill =
    isDgx && isOn
      ? `url(#gold_gradient_${idSuffix})`
      : `url(#paint2_linear_0_1_a10${isOn ? "on" : "off"}_${idSuffix})`;

  return (
    <g transform={`translate(0,${verticalOffset})`}>
      <path
        d="M749.246 686.786V685.087H750.277C750.824 685.087 751.674 685.148 751.674 685.815C751.674 686.544 751.249 686.726 750.581 686.726H749.246M749.246 687.94H749.974L751.674 690.854H753.495L751.613 687.819C752.524 687.758 753.374 687.333 753.374 685.997C753.374 684.358 752.281 683.873 750.399 683.873H747.728V690.854H749.306L749.246 687.94ZM757.077 687.393C757.077 683.266 753.92 680.898 750.339 680.898C746.818 680.898 743.6 683.266 743.6 687.393C743.6 691.522 746.757 693.889 750.339 693.889C753.92 693.889 757.077 691.522 757.077 687.393ZM755.134 687.393C755.134 690.369 752.949 692.372 750.339 692.372C747.667 692.372 745.542 690.369 745.542 687.393C745.542 684.419 747.728 682.416 750.339 682.416C752.949 682.477 755.134 684.48 755.134 687.393ZM510.118 618.187V691.704H530.88V618.187H510.118ZM346.936 618.066V691.643H367.88V634.517L384.21 634.578C389.553 634.578 393.316 635.914 395.866 638.645C399.145 642.106 400.48 647.812 400.48 658.072V691.643H420.756V651.03C420.756 622.012 402.24 618.066 384.21 618.066H346.936ZM543.568 618.187V691.704H577.2C595.109 691.704 600.998 688.729 607.311 682.051C611.804 677.377 614.657 667.056 614.657 655.765C614.657 645.445 612.229 636.217 607.918 630.45C600.329 620.19 589.22 618.187 572.647 618.187H543.568ZM564.148 634.153H573.072C586.003 634.153 594.38 639.92 594.38 655.037C594.38 670.092 586.063 675.92 573.072 675.92H564.148V634.153ZM480.25 618.187L462.948 676.406L446.375 618.187H423.974L447.65 691.704H477.518L501.376 618.187H480.25ZM624.37 691.643H645.132V618.187H624.37V691.643ZM682.528 618.187L653.57 691.643H674.029L678.642 678.712H712.942L717.313 691.643H739.533L710.272 618.187H682.528ZM696.005 631.603L708.572 665.964H683.013L696.005 631.603ZM213.075 630.389V620.312C214.047 620.251 215.079 620.19 216.05 620.19C243.733 619.34 261.824 643.927 261.824 643.927C261.824 643.927 242.276 671.124 221.271 671.124C218.236 671.124 215.564 670.639 213.136 669.788V639.131C223.881 640.467 226.066 645.202 232.562 655.947L246.95 643.806C246.95 643.806 236.448 630.025 218.721 630.025C216.778 630.086 214.897 630.207 213.075 630.389ZM213.075 597V612.055C214.047 611.995 215.079 611.934 216.05 611.873C254.478 610.538 279.551 643.381 279.551 643.381C279.551 643.381 250.775 678.409 220.846 678.409C218.114 678.409 215.564 678.166 213.136 677.742V687.09C215.2 687.333 217.385 687.515 219.571 687.515C247.497 687.515 267.651 673.249 287.199 656.433C290.417 659.043 303.712 665.357 306.444 668.089C287.867 683.63 244.582 696.196 220.057 696.196C217.689 696.196 215.443 696.075 213.197 695.832V708.945H319.253V597H213.075ZM213.075 669.85V677.802C187.275 673.188 180.111 646.355 180.111 646.355C180.111 646.355 192.495 632.635 213.075 630.389V639.131H213.015C202.269 637.796 193.77 647.934 193.77 647.934C193.77 647.934 198.566 664.871 213.075 669.85ZM167.241 645.202C167.241 645.202 182.539 622.679 213.075 620.312V612.116C179.261 614.848 150 643.502 150 643.502C150 643.502 166.573 691.461 213.075 695.893V687.151C178.958 682.84 167.241 645.202 167.241 645.202Z"
        fill={fill}
      />
      {isDgx && (
        <text
          x="50%"
          y="88%"
          dominantBaseline="middle"
          textAnchor="middle"
          fill={fill}
          // fill={`url(#paint1_linear_0_1_a10${isOn ? "on" : "off"}_${idSuffix})`}
          fontSize="100px"
          fontWeight="bold"
          className="select-none"
        >
          DGX Cloud
        </text>
      )}
    </g>
  );
};

const manufacturer = (
  isOn: boolean,
  idSuffix: string,
  name: string,
  isDgx: boolean
) => {
  console.log("hello there", isOn);
  const textFill =
    isDgx && isOn
      ? `url(#paint_dgx_text_${idSuffix})`
      : `url(#paint1_linear_0_1_a10${isOn ? "on" : "off"}_${idSuffix})`;

  return (
    <text
      x="50%"
      y="71%"
      dominantBaseline="middle"
      textAnchor="middle"
      fill={textFill}
      // fill={`url(#paint1_linear_0_1_a10${isOn ? "on" : "off"}_${idSuffix})`}
      fontSize="156px"
      fontWeight="bold"
      className="select-none"
    >
      {name}
    </text>
  );
};
